import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTenantContext } from '../../context/TenantContext';
import { useInstructorAuthContext } from '../../context/InstructorAuthContext';
import { FooterSlot } from '../templates/FooterSlot';
import { 
  LayoutDashboard, 
  BookOpen, 
  Users, 
  TrendingUp, 
  Calendar,
  MessageSquare,
  Settings,
  User,
  Bell,
  Menu,
  X,
  LogOut,
  ChevronsLeft,
  ChevronsRight,
  ChevronDown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
}

interface InstructorLayoutProps {
  children?: React.ReactNode;
}

const InstructorLayout: React.FC<InstructorLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { tenant } = useTenantContext();
  const { logout, instructor: authInstructor, hasPermission } = useInstructorAuthContext();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // TODO: Récupérer les données de l'instructeur connecté
  const instructor = authInstructor ? {
    name: authInstructor.name,
    email: authInstructor.email,
    avatar_url: authInstructor.avatar_url,
    unreadMessages: 0,
    pendingReviews: 0
  } : {
    name: 'Instructeur',
    email: 'instructor@example.com',
    avatar_url: null,
    unreadMessages: 0,
    pendingReviews: 0
  };

  const navigation: NavigationItem[] = [
    { 
      name: 'Tableau de bord', 
      href: '/instructor/dashboard', 
      icon: LayoutDashboard 
    },
    // Visible si permission de voir les cours
    ...(hasPermission('view_courses') ? [{ 
      name: 'Mes cours', 
      href: '/instructor/courses', 
      icon: BookOpen 
    }] as NavigationItem[] : []),
    // Visible si permission de voir les étudiants
    ...(hasPermission('view_students') ? [{ 
      name: 'Étudiants', 
      href: '/instructor/students', 
      icon: Users 
    }] as NavigationItem[] : []),
    // Visible si permission analytics
    ...(hasPermission('analytics_view') ? [{ 
      name: 'Analytics', 
      href: '/instructor/analytics', 
      icon: TrendingUp 
    }] as NavigationItem[] : []),
    { 
      name: 'Calendrier', 
      href: '/instructor/calendar', 
      icon: Calendar 
    },
    ...(hasPermission('send_messages') ? [{ 
      name: 'Messages', 
      href: '/instructor/messages', 
      icon: MessageSquare,
      badge: instructor.unreadMessages 
    }] as NavigationItem[] : [])
  ];

  const isActive = (href: string) => {
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const handleLogout = async () => {
    await logout();
    navigate('/instructor/login');
  };

  const Sidebar = React.memo(({ mobile = false }: { mobile?: boolean }) => (
    <div className={`flex flex-col h-full ${mobile ? 'px-4' : ''}`}>
      {/* Logo + Toggle collapse (desktop) */}
      <div className="flex items-center gap-3 px-6 py-4 border-b justify-between">
        <div className="flex items-center gap-3">
          {tenant?.logo ? (
            <img 
              src={tenant.logo} 
              alt="Logo" 
              className="h-8 w-8 object-contain"
            />
          ) : (
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
          )}
          <div className={`${isCollapsed && !mobile ? 'hidden' : 'block'}`}>
            <h2 className="font-semibold text-lg text-gray-900 dark:text-white">{tenant?.name || 'Site'}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">Espace Enseignant</p>
          </div>
        </div>
        {!mobile && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsCollapsed(prev => !prev);
            }}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800/50 transition-colors"
            aria-label={isCollapsed ? 'Étendre la barre latérale' : 'Réduire la barre latérale'}
          >
            {isCollapsed ? (
              <ChevronsRight className="h-5 w-5 text-white" />
            ) : (
              <ChevronsLeft className="h-5 w-5 text-white" />
            )}
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 py-6 space-y-2">
        {navigation.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          
          return (
            <button
              key={item.name}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (mobile && !isAnimating) {
                  setIsAnimating(true);
                  setTimeout(() => {
                    setSidebarOpen(false);
                    setIsAnimating(false);
                  }, 300);
                }
                navigate(item.href);
              }}
              className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                active
                  ? 'bg-blue-100/50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-800/50'
              } ${isCollapsed && !mobile ? 'justify-center px-2' : ''}`}
            >
              <Icon className={`h-5 w-5 ${active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
              {(!isCollapsed || mobile) && (
                <>
                  <span className="font-medium flex-1 truncate">{item.name}</span>
                  {item.badge && item.badge > 0 && (
                    <Badge variant="destructive" className="ml-auto text-xs">
                      {item.badge}
                    </Badge>
                  )}
                </>
              )}
            </button>
          );
        })}
      </nav>

      {/* Profil instructeur */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-800">
        <div className={`flex items-center gap-3 p-3 rounded-lg ${isCollapsed ? 'justify-center' : ''}`}>
          <Avatar className="h-8 w-8 ring-2 ring-gray-200 dark:ring-gray-700">
            <AvatarImage 
              src={instructor.avatar_url || ''} 
              alt={instructor.name}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const fallback = e.currentTarget.nextElementSibling;
                if (fallback) {
                  (fallback as HTMLElement).style.display = 'flex';
                }
              }}
            />
            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-medium">
              {instructor.name.split(' ').map(n => n[0]).join('')}
            </AvatarFallback>
          </Avatar>
          {(!isCollapsed || mobile) && (
            <div className="flex-1 text-left min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{instructor.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{instructor.email}</p>
            </div>
          )}
        </div>
        {(!isCollapsed || mobile) && (
          <nav className="mt-4 space-y-1">
            <button
              onClick={() => navigate('/instructor/profile')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
            >
              <User className="h-4 w-4" />
              <span className="text-sm">Profil</span>
            </button>
            <button
              onClick={() => navigate('/instructor/settings')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100/50 dark:hover:bg-gray-800/50"
            >
              <Settings className="h-4 w-4" />
              <span className="text-sm">Paramètres</span>
            </button>
          </nav>
        )}
      </div>

      {/* Bouton de déconnexion (raccourci en bas) */}
      <div className="border-t border-gray-200 dark:border-gray-800 p-3">
        <button
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleLogout(); }}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 ${isCollapsed && !mobile ? 'justify-center' : ''}`}
        >
          <LogOut className="h-4 w-4" />
          {(!isCollapsed || mobile) && <span className="text-sm">Déconnexion</span>}
        </button>
      </div>
    </div>
  ));

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Fond d'écran et son overlay (même logique que StudentDashboardPage) */}
      <div 
        className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
        style={{
          backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url(/default-bg.jpg)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/95 via-black/90 to-gray-900/95 backdrop-blur-[2px]"></div>
      </div>
      {/* Motif en superposition */}
      <div 
        className="fixed inset-0 z-0 opacity-5"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '30px 30px'
        }}
      ></div>
      {/* Sidebar Desktop */}
      <div className={`hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:flex-col ${isCollapsed ? 'lg:w-24' : 'lg:w-72'}`}>
        <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900/95 border-r border-gray-700/50">
          <Sidebar />
        </div>
      </div>

      {/* Sidebar Mobile */}
      <div className={`fixed inset-0 z-50 lg:hidden transition-opacity duration-300 ease-in-out ${sidebarOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
        <div 
          className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-in-out"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isAnimating) {
              setIsAnimating(true);
              setTimeout(() => {
                setSidebarOpen(false);
                setIsAnimating(false);
              }, 300);
            }
          }} 
        />
        <div className="fixed inset-0 flex">
          <div 
            className={`relative mr-16 flex w-full max-w-xs transform transition-transform duration-300 ease-in-out ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
          >
            <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
              <button
                type="button"
                className="-m-2.5 p-2.5"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isAnimating) {
                    setIsAnimating(true);
                    setTimeout(() => {
                      setSidebarOpen(false);
                      setIsAnimating(false);
                    }, 300);
                  }
                }}
              >
                <X className="h-6 w-6 text-white" />
              </button>
            </div>
            <div className="flex grow flex-col gap-y-5 overflow-y-auto bg-gray-900/95 px-6 pb-2">
              <Sidebar mobile />
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className={`relative z-10 ${isCollapsed ? 'lg:pl-24' : 'lg:pl-72'}`}>
        {/* Header mobile */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:hidden">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 dark:text-gray-300 lg:hidden"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (!isAnimating) {
                setSidebarOpen(true);
              }
            }}
          >
            <Menu className="h-6 w-6" />
          </button>
          
          <div className="flex flex-1 items-center justify-between">
            <div className="flex items-center gap-3">
              {tenant?.logo ? (
                <img 
                  src={tenant.logo} 
                  alt="Logo" 
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-white" />
                </div>
              )}
              <span className="font-semibold text-gray-900 dark:text-white">{tenant?.name || 'Site'}</span>
            </div>
            
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm">
                <Bell className="h-5 w-5 text-white" />
                {instructor.pendingReviews > 0 && (
                  <Badge variant="destructive" className="ml-1 text-xs">
                    {instructor.pendingReviews}
                  </Badge>
                )}
              </Button>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2">
                    <Avatar className="h-6 w-6">
                      <AvatarImage src={instructor.avatar_url || undefined} />
                      <AvatarFallback className="text-xs">
                        {instructor.name.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent 
                  align="end" 
                  className="w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg overflow-hidden"
                >
                  <div className="px-2 py-1.5 text-sm font-medium text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-700">
                    Mon compte
                  </div>
                  <DropdownMenuItem onClick={() => navigate('/instructor/profile')} className="text-gray-700 dark:text-gray-300">
                    <User className="mr-2 h-4 w-4" />
                    Profil
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/instructor/settings')} className="text-gray-700 dark:text-gray-300">
                    <Settings className="mr-2 h-4 w-4" />
                    Paramètres
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="mr-2 h-4 w-4" />
                    Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>

        {/* Contenu de la page */}
        <main className="py-6 px-4 sm:px-6 lg:px-8">
          {children || <Outlet />}
        </main>
        
        {/* Footer unifié avec l'admin/étudiants */}
        <FooterSlot />
      </div>
    </div>
  );
};

export { InstructorLayout };
